import React, { useMemo, useState, useEffect } from "react";
import Select from "react-select";
import countryDialRows from "../../data/countryDialRows";
import { normalizeDialCodeDigits } from "../../utils/dialCodeFieldNormalize";

/**
 * Admin port of `carehealth/components/common/CountryCodeSelect.js`: same dataset ordering, flags, and
 * full/compact labels. Values here are controlled as dial digits only (no "+") for `contact_person_country_code`.
 * Keep in sync when changing the patient-site component or `countryDialRows` (copy from carehealth/data/country_code_old.js).
 */

function flagEmojiFromCode(code) {
  if (!code || typeof code !== "string") return "";
  const iso = String(code).trim().toUpperCase();
  if (iso.length !== 2) return "";
  const a = iso.charCodeAt(0);
  const b = iso.charCodeAt(1);
  if (a < 0x41 || a > 0x5a || b < 0x41 || b > 0x5a) return "";
  return String.fromCodePoint(0x1f1e6 - 0x41 + a, 0x1f1e6 - 0x41 + b);
}

function compareLocaleStrings(a, b, locale = "en") {
  return String(a).localeCompare(String(b), locale, { sensitivity: "base", numeric: true });
}

function getSortedCountryRows() {
  const list = Array.isArray(countryDialRows) ? countryDialRows : [];
  return [...list].sort((x, y) =>
    compareLocaleStrings(x.name || x.label || "", y.name || y.label || "", "en")
  );
}

function buildDialOptions() {
  return getSortedCountryRows()
    .filter((item) => item.dial_code && String(item.dial_code).trim())
    .map((item) => {
      const flag = (item.unicodeFlag && String(item.unicodeFlag).trim()) || flagEmojiFromCode(item.code);
      const dialCode = String(item.dial_code).trim();
      const dialDigits = normalizeDialCodeDigits(dialCode);
      if (!dialDigits) return null;
      const fullLabel = (
        <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {flag ? (
            <span style={{ fontSize: "1.25em", lineHeight: 1 }} aria-hidden="true">
              {flag}
            </span>
          ) : null}
          <span style={{ color: "#0f172a" }}>{item.name}</span>
          <span style={{ color: "#64748b", fontWeight: 600, marginLeft: "auto" }}>{dialCode}</span>
        </span>
      );
      const compactLabel = (
        <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          {flag ? (
            <span style={{ fontSize: "1.2em", lineHeight: 1 }} aria-hidden="true">
              {flag}
            </span>
          ) : null}
          <span style={{ fontWeight: 700, color: "#0f172a" }}>{dialCode}</span>
        </span>
      );
      return {
        key: item.name,
        value: `${item.name}-${item.dial_code}`,
        label: fullLabel,
        compactLabel,
        dialDigits,
      };
    })
    .filter(Boolean);
}

const DIAL_OPTIONS = buildDialOptions();

function optionForStoredDigits(storedDigits) {
  const d = normalizeDialCodeDigits(storedDigits);
  if (!d) return null;
  return DIAL_OPTIONS.find((o) => o.dialDigits === d) || null;
}

const MENU_Z = 2147483647;

const selectStyles = {
  control: (styles, state) => ({
    ...styles,
    minHeight: 44,
    borderRadius: "10px",
    paddingLeft: "8px",
    paddingRight: "8px",
    border: state.isFocused ? "2px solid #3b82f6" : "2px solid #e5e7eb",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(59, 130, 246, 0.1)" : "none",
    outline: "none",
    width: "100%",
  }),
  menuPortal: (base) => ({ ...base, zIndex: MENU_Z }),
  menu: (base) => ({ ...base, minWidth: 260, zIndex: MENU_Z }),
  menuList: (base) => ({ ...base, maxHeight: "min(280px, 60vh)" }),
  option: (base, state) => ({
    ...base,
    minHeight: 44,
    padding: "10px 12px",
    cursor: "pointer",
    backgroundColor: state.isFocused ? "rgba(59, 130, 246, 0.1)" : base.backgroundColor,
  }),
  singleValue: (base) => ({ ...base, display: "flex", alignItems: "center", margin: 0, gap: "6px" }),
};

/**
 * @param {string} value Dial digits stored on API (e.g. "33", no "+").
 * @param {(digits: string) => void} onChange
 */
function CountryCodeSelect({
  id,
  "aria-label": ariaLabel,
  value,
  onChange,
  disabled = false,
  placeholder = "Country code",
}) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const selected = useMemo(() => optionForStoredDigits(value), [value]);

  const instanceId = useMemo(() => `country_code_admin_${id || "default"}`, [id]);

  if (!mounted) {
    return (
      <div
        id={id}
        style={{
          minHeight: 44,
          borderRadius: "10px",
          paddingLeft: "8px",
          border: "2px solid #e5e7eb",
          display: "flex",
          alignItems: "center",
          color: "#999",
          width: "100%",
        }}
      >
        {placeholder}
      </div>
    );
  }

  return (
    <div className="country-code-select-wrapper" style={{ position: "relative", width: "100%", minWidth: 0 }}>
      <Select
        inputId={id}
        aria-label={ariaLabel || placeholder}
        instanceId={instanceId}
        classNamePrefix="country-code-select"
        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
        menuPosition="fixed"
        styles={selectStyles}
        placeholder={placeholder}
        isClearable
        isDisabled={disabled}
        options={DIAL_OPTIONS}
        value={selected}
        getOptionValue={(o) => o.value}
        formatOptionLabel={(option, meta) => {
          if (meta.context === "value" && option.compactLabel) return option.compactLabel;
          return option.label;
        }}
        onChange={(opt) => onChange(opt ? opt.dialDigits : "")}
      />
    </div>
  );
}

export default CountryCodeSelect;
